/**
 * Raw JobApplication data types as returned from the backend
 * These types match the GraphQL schema structure directly
 */

/**
 * Display mode for job applications
 * - 'full': Show both talent and job offer details (main job applications page)
 * - 'job-focus': Emphasize job offer details, hide/minimize talent info (when viewing a specific talent's applications)
 * - 'talent-focus': Emphasize talent details, hide/minimize job offer info (when viewing applications for a specific job)
 */
export type JobApplicationDisplayMode = 'full' | 'job-focus' | 'talent-focus';

export enum Degree {
    NO_DIPLOMA = "C'est pas les diplômes qui comptent !",
    CAP_BEP = "CAP / BEP",
    BP = "Baccalauréat Professionnel",
    BAC_1 = "Baccalauréat",
    BAC_2 = "BTS, DEUG, DUT, DEUST (Bac+2)",
    BAC_3 = "Licence, Licence Pro, BUT (Bac+3)",
    BAC_4 = "Maîtrise, Master 1 (Bac+4)",
    BAC_5 = "Master, DESS, Ingénieur (Bac+5)",
    BAC_6 = "BAC+6",
    DOCTORATE = "Doctorat",
    CHERCHEUR = "Chercheur",
    ADDITIONAL_MENTION = "Mention complémentaire",
    // Old Version
    // TODO: Remove this after migration db
    OLD_DNB = "DNB",
    OLD_BACCALAUREAT_BP = "Baccalauréat, BP",
    OLD_DOCTORATE_BAC_5 = "Doctorat (>Bac+5)",
}

export interface JobApplicationRaw {
    _id: number;
    jobApplicationNumber?: string;
    status: string;
    subStatus?: string;
    date: string;
    rating?: number | null;
    resumeScore?: number | null;
    resumeExplainer?: string | null;
    resumeScoreError?: string | null;
    scoringStatus?: 'pending_cv' | 'scoring' | 'done' | 'error' | null;
    type?: string;
    upercutAverageRating?: number | null;
    upercutRatings?: Array<{
        upercutId: string;
        rating: number;
        createdAt: string;
    }>;
    upercutNotes?: Array<{
        _id: string;
        upercutId: string;
        title: string;
        note: string;
        visibility: 'public' | 'private';
        createdAt: string;
    }>;
    jobOffer: {
        _id: string;
        title: string;
        city: string;
        jobNumber: string;
        active: boolean;
        contract: {
            status: string;
            remuneration: {
                min: number;
                max: number;
            };
            rateType: string;
            isAnnualSalary?: boolean;
        };
        position: {
            experience: string;
        };
        candidate: {
            degree: string[];
        };
        brand: {
            _id: string;
            brandName: string;
            brandLogo: {
                url: string;
            };
        };
        pro: {
            _id: string;
            name: string;
            commercialName: string;
            brandLogo: {
                url: string;
            };
        };
    };
    talent: {
        _id: string;
        firstName: string;
        lastName: string;
        email: string;
        photo?: string | null;
        logo?: string | null;
        active?: boolean;
        city: string;
        degree: string[];
        yearsOfExperience?: number;
        currentStatusUser?: string;
        resumeId?: string | null;
        upercutFavorites?: Array<{
            _id: string;
            type: string;
            upercutId?: string;
            createdAt: string;
        }>;
        upercutFiles?: Array<{ _id: string }>;
        principalProfession?: {
            _id: string;
            professionName: string;
        } | null;
        experiences?: Array<{
            _id: string;
            title: string;
            enterprise?: { name: string } | null;
            startDate?: string;
            endDate?: string | null;
        }>;
        resume?: {
            path: string;
        }
    };
}

/**
 * Helper function to get the highest degree from a list of degrees
 */
export const getHighestDegreeLabel = (degrees: string[]): string => {
    const degreeOrder: { [key: string]: number } = {
        [Degree.NO_DIPLOMA]: 0,
        [Degree.CAP_BEP]: 1,
        [Degree.BP]: 2,
        [Degree.BAC_1]: 3,
        [Degree.BAC_2]: 4,
        [Degree.BAC_3]: 5,
        [Degree.BAC_4]: 6,
        [Degree.BAC_5]: 7,
        [Degree.BAC_6]: 8,
        [Degree.DOCTORATE]: 9,
        [Degree.CHERCHEUR]: 10,
        [Degree.ADDITIONAL_MENTION]: 11,
        // Old Version
        [Degree.OLD_DNB]: 0,
        [Degree.OLD_BACCALAUREAT_BP]: 3,
        [Degree.OLD_DOCTORATE_BAC_5]: 9,
    };

    let highestDegree = '';
    let highestRank = -1;

    degrees.forEach(degree => {
        const rank = degreeOrder[degree];
        if (rank !== undefined && rank > highestRank) {
            highestRank = rank;
            highestDegree = degree;
        }
    });

    return highestDegree || 'N/A';
};

/**
 * Represents a job application selected in a multi-select context
 */
export interface MultiSelectJobApplication {
    _id: string;
    status?: string;
    talentId?: string;
}

/**
 * Kanban column data structure for infinite scroll
 */
export interface KanbanColumnData {
    items: JobApplicationRaw[];
    hasMore: boolean;
    loading: boolean;
    page: number;
    totalDocs: number;
}

/**
 * Kanban data structure organized by status
 */
export type KanbanData = Record<string, KanbanColumnData>;

